import React from 'react'
import { 
  User as UserIcon, Mail, Calendar, Shield, 
  UserCheck, UserX, Clock, Activity 
} from 'lucide-react'
import Button from '../ui/Button'
import { User } from '../../types'
import {
  formatarTipoUsuario,
  getTipoUsuarioColor,
  getTipoUsuarioIcon,
  formatarDataLogin
} from '../../services/usuarios'

interface UsuarioDetailsProps {
  usuario: User
  onEdit?: () => void
  onClose: () => void
  canEdit?: boolean
}

const UsuarioDetails: React.FC<UsuarioDetailsProps> = ({
  usuario,
  onEdit,
  onClose,
  canEdit = false
}) => {
  const tipoConfig = getTipoUsuarioColor(usuario.tipo)
  const tipoIcon = getTipoUsuarioIcon(usuario.tipo)

  const formatarData = (data: string | Date) => {
    return new Date(data).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header com avatar e informações básicas */}
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
            <span className="text-xl font-bold text-gray-700">
              {usuario.nome.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {usuario.nome}
          </h3>
          <div className="flex items-center space-x-2 mt-1">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">{usuario.email}</span>
          </div>
          <div className="flex items-center space-x-4 mt-2">
            {/* Tipo de utilizador */}
            <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
              tipoConfig === 'red' ? 'bg-red-100 text-red-800' :
              tipoConfig === 'blue' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              <span className="mr-1">{tipoIcon}</span>
              {formatarTipoUsuario(usuario.tipo)}
            </span>
            
            {/* Status */}
            <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
              usuario.ativo 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {usuario.ativo ? (
                <>
                  <UserCheck className="h-3 w-3 mr-1" />
                  Ativo
                </>
              ) : (
                <>
                  <UserX className="h-3 w-3 mr-1" />
                  Inativo
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Informações detalhadas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Informações pessoais */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <UserIcon className="h-4 w-4 mr-2" />
            Informações Pessoais
          </h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-700">ID:</span>
              <span className="ml-2 text-gray-600">{usuario.id}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Nome:</span>
              <span className="ml-2 text-gray-600">{usuario.nome}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Email:</span>
              <span className="ml-2 text-gray-600">{usuario.email}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Tipo:</span>
              <span className="ml-2 text-gray-600">{formatarTipoUsuario(usuario.tipo)}</span>
            </div>
          </div>
        </div>

        {/* Informações do sistema */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <Activity className="h-4 w-4 mr-2" />
            Informações do Sistema
          </h4>
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`ml-2 ${
                usuario.ativo ? 'text-green-600' : 'text-red-600'
              }`}>
                {usuario.ativo ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Último Login:</span>
              <span className="ml-2 text-gray-600">
                {formatarDataLogin(usuario.ultimoLogin)}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Criado em:</span>
              <span className="ml-2 text-gray-600">
                {formatarData(usuario.createdAt)}
              </span>
            </div>
            {usuario.updatedAt && (
              <div>
                <span className="font-medium text-gray-700">Atualizado em:</span>
                <span className="ml-2 text-gray-600">
                  {formatarData(usuario.updatedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Permissões baseadas no tipo */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
          <Shield className="h-4 w-4 mr-2" />
          Permissões do Utilizador
        </h4>
        <div className="text-sm text-gray-600">
          {usuario.tipo === 'ADMIN' && (
            <ul className="list-disc list-inside space-y-1">
              <li>Acesso total ao sistema</li>
              <li>Gestão de utilizadores</li>
              <li>Configurações do sistema</li>
              <li>Relatórios e dashboard</li>
              <li>Gestão de clientes e equipamentos</li>
              <li>Ordens de serviço e vendas</li>
            </ul>
          )}
          {usuario.tipo === 'TECNICO' && (
            <ul className="list-disc list-inside space-y-1">
              <li>Gestão de ordens de serviço</li>
              <li>Gestão de clientes e equipamentos</li>
              <li>Consulta de relatórios</li>
              <li>Gestão de stock (consulta)</li>
            </ul>
          )}
          {usuario.tipo === 'VENDEDOR' && (
            <ul className="list-disc list-inside space-y-1">
              <li>Gestão de vendas</li>
              <li>Gestão de clientes</li>
              <li>Consulta de produtos</li>
              <li>Relatórios de vendas</li>
            </ul>
          )}
        </div>
      </div>

      {/* Botões de ação */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          variant="outline"
          onClick={onClose}
        >
          Fechar
        </Button>
        {canEdit && onEdit && (
          <Button
            onClick={onEdit}
          >
            Editar Utilizador
          </Button>
        )}
      </div>
    </div>
  )
}

export default UsuarioDetails